/**
 * Trajectory fitting for the golf-ball flight tracker. Per-frame ball
 * *detection* lives in ballDetector.ts (a YOLOv8 model fine-tuned
 * specifically on golf balls, run via ONNX Runtime Web); this module takes
 * the resulting noisy,
 * multi-candidate-per-frame data and reconstructs which candidates form a
 * real, physically plausible flight versus incidental clutter (a spare
 * ball lying in the grass, a bright cap) that the object detector still
 * correctly reports as "a ball" without knowing which one is airborne.
 */

export interface Point {
  /** Relative 0..1 position within the frame */
  x: number;
  y: number;
  /** Timestamp in seconds within the source video/stream */
  t: number;
}

/**
 * Removes candidates that keep reappearing in roughly the same spot across
 * many frames — e.g. a spare ball lying in the grass, or a light patch of
 * background. A real ball in flight occupies any given spot only
 * fleetingly, so a position that recurs across a large fraction of all
 * sampled frames is much more likely to be a persistent object than the
 * ball, and left in would otherwise out-vote the brief, genuine flight in
 * the RANSAC inlier count below.
 */
export function filterTransientCandidates(
  candidatesByFrame: Point[][],
  radius = 0.06,
  maxPersistence = 0.35
): Point[][] {
  const frames = candidatesByFrame.filter((f) => f.length > 0);
  if (frames.length < 4) return candidatesByFrame;

  const maxFrames = Math.max(1, Math.round(frames.length * maxPersistence));

  // The frame-count threshold above works for a clip that's mostly just the
  // flight, but on a longer clip (practice swing, walk-up, dead time after
  // the shot, or a stretch where the detector missed everything) 35% of
  // *sampled-with-a-hit* frames can still amount to many seconds of real
  // time — far longer than anything genuinely airborne ever holds one
  // screen position. This measures persistence directly in seconds — from
  // the first sampled moment a position is seen to the last — which stays
  // correct even across a long gap of zero detections in between (a raw
  // frame-count proxy for "seconds" would under/overshoot there, since gaps
  // mean the same number of hits can span very different real durations).
  // The visible part of a real flight (measured, not guessed: ~0.45s in
  // test footage) can itself span close to half a second, so this must sit
  // comfortably above that — a cap tight enough to bite real flight data
  // would defeat the point.
  const maxPersistenceSeconds = 0.6;

  function frameCountNear(p: Point): number {
    let count = 0;
    for (const frame of frames) {
      if (frame.some((c) => Math.abs(c.x - p.x) < radius && Math.abs(c.y - p.y) < radius)) count++;
    }
    return count;
  }

  function nearTimeSpan(p: Point): number {
    let minT = Infinity;
    let maxT = -Infinity;
    for (const frame of frames) {
      if (!frame.some((c) => Math.abs(c.x - p.x) < radius && Math.abs(c.y - p.y) < radius)) continue;
      const t = frame[0].t;
      if (t < minT) minT = t;
      if (t > maxT) maxT = t;
    }
    return maxT - minT;
  }

  // All of the persistence heuristics above assume "recurs near the same
  // spot for a while" means clutter — true for an actually-resting ball,
  // but a slow-moving one (a putt, a rolling shot, the early, near-vertical
  // part of a swing) can *also* stay within one matching radius for a
  // while despite genuinely moving the whole time. So measure the actual
  // spread of matched positions within a short time window, and treat
  // clear spread as motion, not clutter — but ONLY as a way to rescue a
  // position whose total presence is still bounded to a plausible flight
  // window (see absoluteMaxSpan below), never as a blanket override. A
  // detector's confidence — and with it, the exact reported pixel — can
  // wobble on an object that never actually moves, so across hundreds of
  // frames some noisy pair within a 300+-frame decoy's own jitter will
  // eventually exceed almost any spread threshold by pure chance; letting
  // that alone waive every other check reopens the door to exactly the
  // multi-second persistent clutter these checks exist to catch.
  const stillnessSpread = 0.03;
  const stillnessWindow = 1;
  // No real flight is visible anywhere close to this long — this is a hard
  // ceiling that no amount of local "movement" can talk its way past, which
  // is what actually rules out a decoy that happens to wobble/drift a bit
  // within an otherwise multi-second presence (a slow pan, a nudged ball).
  // A fixed matching radius (not a time-aware one) can pull in one
  // coincidentally-nearby real point from well after the flight itself
  // ended and inflate this measurement, so this needs real headroom above
  // the longest plausible flight — the multi-second decoys this exists to
  // catch clear it by 3x or more regardless.
  const absoluteMaxSpan = 4;

  function nearSpread(p: Point): number {
    const matches: Point[] = [];
    for (const frame of frames) {
      const match = frame.find((c) => Math.abs(c.x - p.x) < radius && Math.abs(c.y - p.y) < radius);
      if (match) matches.push({ ...match, t: frame[0].t });
    }
    let maxSpread = 0;
    for (let i = 0; i < matches.length; i++) {
      for (let j = i + 1; j < matches.length; j++) {
        if (Math.abs(matches[i].t - matches[j].t) > stillnessWindow) continue;
        const d = Math.max(Math.abs(matches[i].x - matches[j].x), Math.abs(matches[i].y - matches[j].y));
        if (d > maxSpread) maxSpread = d;
      }
    }
    return maxSpread;
  }

  // A ball is airborne for well under a second, so it can never show up
  // near both the start AND the end of a longer clip — only furniture (a
  // spare ball left lying in the grass, a range marker) does that. This
  // catches persistent objects a slow handheld pan has nudged across the
  // frame, which the same-radius check above can miss since no single spot
  // ever accumulates enough hits while the object gradually drifts.
  const allTs = frames.map((f) => f[0].t);
  const minT = Math.min(...allTs);
  const maxT = Math.max(...allTs);
  const span = maxT - minT;
  const earlyEnd = minT + span * 0.25;
  const lateStart = maxT - span * 0.25;
  const driftRadius = radius * 2.5;
  const earlyFrames = frames.filter((f) => f[0].t <= earlyEnd);
  const lateFrames = frames.filter((f) => f[0].t >= lateStart);

  function seenNear(p: Point, windowFrames: Point[][]): boolean {
    return windowFrames.some((frame) => frame.some((c) => Math.abs(c.x - p.x) < driftRadius && Math.abs(c.y - p.y) < driftRadius));
  }

  function isBookendClutter(p: Point): boolean {
    if (span <= 0 || earlyFrames.length === 0 || lateFrames.length === 0) return false;
    return seenNear(p, earlyFrames) && seenNear(p, lateFrames);
  }

  // nearSpread only catches motion that's slow enough for consecutive real
  // samples to still land within one matching radius of each other. A ball
  // moving fast right after impact covers more than that radius between
  // sampled frames, so it never accumulates the multi-point spread nearSpread
  // looks for — and can then get coincidentally caught by the bookend check
  // just for happening to sit, at its own single moment in time, within the
  // (generous, drift-tolerant) radius of two unrelated things at the clip's
  // edges. The fix is a second, time-based reading of "is this moving":
  // look for a large jump to whatever else was seen shortly before or after
  // *this specific frame*, regardless of where in the clip that is.
  // Checking "is anything far away nearby in time" alone isn't enough — a
  // second, unrelated stationary object elsewhere in frame (a spare ball,
  // a decoy) is *also* "far away" from p in every nearby frame simply by
  // coexisting there, which would rescue every point of every decoy
  // whenever more than one persistent object is in shot. The distinguishing
  // signal is the *absence* of anything close to p in that nearby frame —
  // a decoy's own continuation (nearly the same spot, one sample later)
  // would show up as a close match there; a real ball that's moved on
  // leaves none.
  const fastMotionWindow = 0.1;

  function isFastMotion(p: Point): boolean {
    for (const frame of frames) {
      const ft = frame[0].t;
      if (Math.abs(ft - p.t) > fastMotionWindow) continue;
      let hasClose = false;
      let hasFar = false;
      for (const c of frame) {
        const d = Math.max(Math.abs(c.x - p.x), Math.abs(c.y - p.y));
        if (d < radius) hasClose = true;
        if (d > stillnessSpread) hasFar = true;
      }
      if (hasFar && !hasClose) return true;
    }
    return false;
  }

  return candidatesByFrame.map((frame) =>
    frame.filter((p) => {
      // Hard ceiling first: nothing genuinely airborne is trackable-in-this-
      // loose-radius-sense this long, and unlike the checks below, no local
      // "movement" reading can talk its way past it.
      if (nearTimeSpan(p) > absoluteMaxSpan) return false;
      // Clear, fast local movement means this isn't the resting/slowly-
      // drifting clutter the checks below are aimed at — a coincidental
      // late-appearing neighbor (bookend check) or a longer-than-typical
      // but still bounded presence (duration check) don't disqualify real
      // motion once it's been positively identified as motion.
      if (nearSpread(p) > stillnessSpread || isFastMotion(p)) return true;
      return frameCountNear(p) <= maxFrames && nearTimeSpan(p) <= maxPersistenceSeconds && !isBookendClutter(p);
    })
  );
}

export type Trajectory = (t: number) => { x: number; y: number };

/**
 * Least-squares fit x = a*t + b. Used for the horizontal component of
 * flight: over the fraction of a second a ball is actually airborne,
 * constant-velocity is a fine approximation, and — critically — this stays
 * well-defined even when x barely changes (see fitTrajectory below).
 */
function fitLinear(points: Point[]): ((t: number) => number) | null {
  const n = points.length;
  if (n < 2) return null;
  let St = 0,
    St2 = 0,
    Sx = 0,
    Stx = 0;
  for (const p of points) {
    St += p.t;
    St2 += p.t * p.t;
    Sx += p.x;
    Stx += p.t * p.x;
  }
  const denom = n * St2 - St * St;
  if (Math.abs(denom) < 1e-9) return null;
  const a = (n * Stx - St * Sx) / denom;
  const b = (Sx - a * St) / n;
  return (t: number) => a * t + b;
}

/**
 * Fits ball position as a function of *time* rather than the classic
 * y = f(x) parabola: x(t) linear, y(t) quadratic (gravity). A phone filmed
 * side-on to the shot gives a trajectory with lots of horizontal spread, but
 * most casual footage is shot from behind/beside the golfer looking down
 * the target line, where the ball moves almost straight up-then-down on
 * screen with barely any horizontal parallax — a y = f(x) parabola is
 * degenerate for that (x barely varies), even though the flight is
 * perfectly real. Parameterizing both axes by time handles either framing.
 */
export function fitTrajectory(points: Point[]): Trajectory | null {
  if (points.length < 3) return null;
  const yOfT = fitParabola(points.map((p) => ({ x: p.t, y: p.y, t: p.t })));
  const xOfT = fitLinear(points);
  if (!yOfT || !xOfT) return null;
  return (t: number) => ({ x: xOfT(t), y: yOfT(t) });
}

export interface LandingEstimate {
  /** Timestamp (seconds, same axis as the input points) of the estimated landing. */
  t: number;
  x: number;
  y: number;
}

/**
 * Extends a fitted trajectory past the last real detection to a rough
 * landing estimate, by walking the already-fitted parabola forward until it
 * comes back down to roughly the height it started at. Most footage only
 * has the ball clearly visible for a fraction of a second — far short of a
 * full flight — so this trades a real measurement for a physics-based
 * guess: same curve shape the real data already implies, just followed
 * further. Only ever used to *draw* an estimated continuation, never
 * treated as a detection.
 */
export function extrapolateLanding(
  curve: Trajectory,
  points: Point[],
  options: { maxExtraDuration?: number; step?: number } = {}
): LandingEstimate | null {
  if (points.length < 3) return null;
  const ts = points.map((p) => p.t);
  const maxT = Math.max(...ts);
  const observedDuration = maxT - Math.min(...ts);
  if (observedDuration <= 0) return null;

  // The ball's height at the earliest real detection is the best available
  // stand-in for "ground level" — on a flat tee/fairway the camera frames
  // roughly the same ground height near the golfer and out where the ball
  // comes down. Not true on sloped terrain, which is exactly why this is
  // presented as an estimate, not a measurement.
  const groundY = Math.max(...points.map((p) => p.y));

  // Only extrapolate a curve that's already descending at its last real
  // point — one still on the way up hasn't reached an apex yet, so
  // projecting it forward isn't meaningful (and for a short, still-rising
  // window the fitted parabola's curvature is especially unreliable).
  const step = options.step ?? 0.02;
  const yAtMax = curve(maxT).y;
  const yBeforeMax = curve(Math.max(0, maxT - step)).y;
  if (yAtMax <= yBeforeMax) return null;
  if (yAtMax >= groundY) return null;

  // A fit built from a brief, noisy window becomes unreliable the further
  // it's stretched — cap how far forward this is willing to guess rather
  // than let small curvature errors compound into an absurd landing spot.
  const maxExtraDuration = options.maxExtraDuration ?? Math.max(1.5, observedDuration * 4);
  const limit = maxT + maxExtraDuration;

  let t = maxT;
  let prevY = yAtMax;
  while (t < limit) {
    const nextT = t + step;
    const nextY = curve(nextT).y;
    if (nextY >= groundY) {
      const frac = (groundY - prevY) / (nextY - prevY);
      const landingT = t + frac * step;
      const landingPoint = curve(landingT);
      return { t: landingT, x: landingPoint.x, y: landingPoint.y };
    }
    t = nextT;
    prevY = nextY;
  }
  return null;
}

/**
 * Robustly fits a flight trajectory across many frames' worth of noisy
 * candidates. Repeatedly samples 3 random candidates (from 3 different
 * frames), fits a trajectory through them, and counts how many *other*
 * frames have at least one candidate close to it. The trajectory with the
 * most agreeing frames wins — this is what filters out remaining noise
 * (detector false positives, spare balls the persistence filter missed),
 * since real ball detections line up on one consistent path while noise
 * doesn't.
 */
export function ransacTrajectory(
  candidatesByFrame: Point[][],
  options: {
    iterations?: number;
    tolerance?: number;
    minDisplacement?: number;
    maxFlightDuration?: number;
    maxGap?: number;
  } = {}
): { curve: Trajectory; inliers: Point[] } | null {
  const iterations = options.iterations ?? 4000;
  const tolerance = options.tolerance ?? 0.02;
  // Guards against a perfectly degenerate (zero-slope) fit slipping through
  // — real stationary clutter is already removed upstream by
  // filterTransientCandidates, so this only needs to rule out "no motion at
  // all", not require any particular amount of it. A ball that's airborne
  // for a fraction of a second and far from the camera can cover well under
  // 5% of the frame before it's too small to detect any further, so this
  // must stay far smaller than the old heuristic-era threshold.
  const minDisplacement = options.minDisplacement ?? 0.01;
  // A detector's confidence for an object can fluctuate frame to frame even
  // when nothing is actually moving, so a spare ball lying in the grass can
  // occasionally slip through the persistence filter as a sparse, spread-out
  // scatter of "detections" instead of a dense, obviously-recurring one.
  // Physics is the backstop: a golf shot's visible hang time is at most a
  // few seconds, and — critically — the detections that make it up should
  // be roughly continuous (allowing for the odd missed frame), not spread
  // thinly across a much longer stretch of the clip with big silent gaps.
  const maxFlightDuration = options.maxFlightDuration ?? 3;
  // Measured, not guessed: real clusters seen in test footage have internal
  // gaps up to ~0.22s (a couple of consecutive missed detections) but never
  // anywhere close to half a second — a gap that large is a sign the
  // "inliers" are really two unrelated things (a brief decoy here, an
  // unrelated blip there) that only coincidentally lie near the same fitted
  // curve, not one continuous observation of the same moving object.
  const maxGap = options.maxGap ?? 0.6;
  // A fitted curve is only ever really validated by the few points it was
  // built from — evaluated far outside that time range, it's pure
  // extrapolation, and with enough unrelated stationary clutter in a clip
  // (a decoy ball near the start, a different one near the end) the
  // extrapolated line can easily sweep close to both by coincidence,
  // stitching two unrelated stationary objects into one fake "trajectory"
  // that only *looks* continuous because gap/duration are checked on the
  // resulting inliers rather than on how far they are from real support.
  // Capping how far past its own seed a curve may collect inliers keeps
  // "support" meaning support, not extrapolation reach.
  const windowMargin = 0.5;

  const frames = candidatesByFrame.filter((f) => f.length > 0);
  if (frames.length < 4) return null;

  // A 3-point seed always fits a parabola exactly — by construction, it
  // "agrees with itself" no matter which 3 candidates it happened to draw,
  // real or noise — so a bare "did we find *a* fit" was never a strong
  // enough bar (see minSupport below). This still tracks a single
  // trajectory unconditionally across the whole search — the same
  // highest-vote-count approach that reliably converges, since gating
  // candidates on confidence *during* the search instead makes the random
  // search itself unreliable: a 3-point seed's curve only extrapolates
  // well to the *rest* of its own real cluster if those 3 points happen to
  // be well-spread across it, so requiring every draw to already qualify
  // on its own means most genuine seeds get thrown away before they get a
  // chance to accumulate support. Confidence is checked exactly once, at
  // the very end, against whichever single trajectory won.
  // A fixed floor, not a fraction of frames.length: how large a *genuine*
  // flight cluster can be is bounded by physics (sample rate × how long a
  // ball stays visible), not by how much unrelated clutter (decoys, stray
  // false positives) happens to also be in the clip. Scaling the bar with
  // total candidate frames means a busier clip demands a *bigger* real
  // cluster to compensate for clutter it doesn't actually add — a clip
  // with two persistent decoys can easily have 3x the candidate frames of
  // one without, without the real flight itself being any longer, so a
  // fractional bar can end up demanding more of the real cluster than it
  // could ever physically contain. The other checks (seed spread, absolute
  // span, bookend/fast-motion) already carry the job of rejecting clutter.
  const minSupport = Math.max(5, Math.min(12, frames.length));

  let bestCount = 0;
  let bestTrajectory: Trajectory | null = null;
  let bestSeed: Point[] | null = null;

  for (let iter = 0; iter < iterations; iter++) {
    const a = frames[(Math.random() * frames.length) | 0];
    const b = frames[(Math.random() * frames.length) | 0];
    const c = frames[(Math.random() * frames.length) | 0];
    if (a === b || b === c || a === c) continue;

    const sample = [a[(Math.random() * a.length) | 0], b[(Math.random() * b.length) | 0], c[(Math.random() * c.length) | 0]];
    const seedFrames = new Set([a, b, c]);
    const sampleTs = sample.map((p) => p.t).sort((x, y) => x - y);
    const tMin = sampleTs[0];
    const tMax = sampleTs[sampleTs.length - 1];
    if (tMax - tMin > maxFlightDuration) continue;
    // Reject seeds that are themselves temporally spread thin — a trajectory
    // built from 3 widely-scattered noise points can rack up more incidental
    // "votes" than the real, tightly-clustered flight simply by being loose
    // enough to pass near other unrelated noise, so this has to be enforced
    // at the seed stage rather than only checked after the fact on whichever
    // fit happened to win the vote count.
    if (sampleTs[1] - sampleTs[0] > maxGap || sampleTs[2] - sampleTs[1] > maxGap) continue;

    // A ball genuinely in flight is always moving, so any two of its
    // samples — even just a couple hundredths of a second apart — sit at
    // visibly different positions. A stationary decoy (a spare ball lying
    // in the grass) sampled at two different times instead lands on almost
    // the exact same pixel twice. Without this check, seeds built from two
    // hits on the same decoy plus one real point exploit that: the fitted
    // curve trivially "explains" every frame the decoy sits in (dozens of
    // them, since it never moves), racking up far more incidental votes
    // than the real, brief, few-frame trajectory ever could.
    const minSeedSpread = 0.004;
    const d01 = Math.hypot(sample[0].x - sample[1].x, sample[0].y - sample[1].y);
    const d12 = Math.hypot(sample[1].x - sample[2].x, sample[1].y - sample[2].y);
    const d02 = Math.hypot(sample[0].x - sample[2].x, sample[0].y - sample[2].y);
    if (d01 < minSeedSpread || d12 < minSeedSpread || d02 < minSeedSpread) continue;

    const trajectory = fitTrajectory(sample);
    if (!trajectory) continue;
    const p0 = trajectory(tMin);
    const p1 = trajectory(tMax);
    if (Math.hypot(p1.x - p0.x, p1.y - p0.y) < minDisplacement) continue;

    // The 3 seed points define the trajectory by construction, so they
    // count as support automatically — x(t) is a 2-parameter line fit
    // through however many points support it, which for a bare 3-point
    // seed is over-determined (not an exact interpolation) and can leave
    // a few hundredths of real residual even at the seed's own points, so
    // re-checking them against the same tight tolerance as everything
    // else would sometimes reject a seed against the data that defined it.
    const windowStart = tMin - windowMargin;
    const windowEnd = tMax + windowMargin;
    let count = seedFrames.size;
    for (const frame of frames) {
      if (seedFrames.has(frame)) continue;
      const ft = frame[0].t;
      if (ft < windowStart || ft > windowEnd) continue;
      for (const cand of frame) {
        const pred = trajectory(cand.t);
        if (Math.hypot(pred.x - cand.x, pred.y - cand.y) < tolerance) {
          count++;
          break;
        }
      }
    }
    if (count > bestCount) {
      bestCount = count;
      bestTrajectory = trajectory;
      bestSeed = sample;
    }
  }

  if (!bestTrajectory || !bestSeed) return null;

  // The winning seed's own 3 points count by construction (see above) —
  // include them directly rather than re-testing them against the same
  // tolerance used for everything else. Same time-window cap as during
  // voting: only frames near the seed's own span can join as inliers, so
  // the refined curve can't get dragged toward unrelated clutter that
  // merely happened to lie near this fit's far extrapolation.
  const bestSeedTimes = new Set(bestSeed.map((p) => p.t));
  const bestSeedTs = bestSeed.map((p) => p.t);
  const bestWindowStart = Math.min(...bestSeedTs) - windowMargin;
  const bestWindowEnd = Math.max(...bestSeedTs) + windowMargin;
  const inliers: Point[] = [...bestSeed];
  for (const frame of frames) {
    if (frame.some((p) => bestSeedTimes.has(p.t))) continue;
    const ft = frame[0].t;
    if (ft < bestWindowStart || ft > bestWindowEnd) continue;
    let best: Point | null = null;
    let bestDist = Infinity;
    for (const cand of frame) {
      const pred = bestTrajectory(cand.t);
      const d = Math.hypot(pred.x - cand.x, pred.y - cand.y);
      if (d < tolerance && d < bestDist) {
        bestDist = d;
        best = cand;
      }
    }
    if (best) inliers.push(best);
  }

  if (inliers.length < 3) return null;

  const sortedInlierTs = inliers.map((p) => p.t).sort((x, y) => x - y);
  if (sortedInlierTs[sortedInlierTs.length - 1] - sortedInlierTs[0] > maxFlightDuration) return null;
  let maxConsecutiveGap = 0;
  for (let i = 1; i < sortedInlierTs.length; i++) {
    const gap = sortedInlierTs[i] - sortedInlierTs[i - 1];
    if (gap > maxGap) return null;
    if (gap > maxConsecutiveGap) maxConsecutiveGap = gap;
  }

  // The confidence check, applied once here against the single winner of
  // the whole search (see above for why not sooner). A gapless run — every
  // sampled frame in the stretch agreeing, not just several scattered
  // ones — is trusted at a lower count than the fractional bar requires:
  // real, consecutive detections tend to come as an unbroken run right
  // where the ball is briefly visible, while noise achieving the same
  // count is almost always scattered with gaps, since nothing keeps
  // producing a false positive in the exact right spot on every
  // consecutive frame.
  const isGaplessBurst = inliers.length >= 4 && maxConsecutiveGap <= 0.04;
  if (!isGaplessBurst && inliers.length < minSupport) return null;

  const refined = fitTrajectory(inliers) ?? bestTrajectory;

  const inlierTs = inliers.map((p) => p.t);
  const start = refined(Math.min(...inlierTs));
  const end = refined(Math.max(...inlierTs));
  if (Math.hypot(end.x - start.x, end.y - start.y) < minDisplacement) return null;

  return { curve: refined, inliers };
}

/** Quadratic least-squares fit y = a·x² + b·x + c through the given points. */
export function fitParabola(points: Point[]): ((x: number) => number) | null {
  if (points.length < 3) return null;

  let Sx1 = 0,
    Sx2 = 0,
    Sx3 = 0,
    Sx4 = 0,
    Sy0 = 0,
    Sxy1 = 0,
    Sxy2 = 0;
  const Sx0 = points.length;

  for (const p of points) {
    const x = p.x,
      y = p.y;
    const x2 = x * x,
      x3 = x2 * x,
      x4 = x2 * x2;
    Sx1 += x;
    Sx2 += x2;
    Sx3 += x3;
    Sx4 += x4;
    Sy0 += y;
    Sxy1 += x * y;
    Sxy2 += x2 * y;
  }

  const A = [
    [Sx4, Sx3, Sx2],
    [Sx3, Sx2, Sx1],
    [Sx2, Sx1, Sx0],
  ];
  const B = [Sxy2, Sxy1, Sy0];
  const solved = solve3x3(A, B);
  if (!solved) return null;
  const [a, b, c] = solved;
  return (x: number) => a * x * x + b * x + c;
}

function det3(m: number[][]): number {
  return (
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
    m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
    m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0])
  );
}

function solve3x3(A: number[][], B: number[]): [number, number, number] | null {
  const D = det3(A);
  if (Math.abs(D) < 1e-9) return null;
  const replace = (col: number) => A.map((row, i) => row.map((v, j) => (j === col ? B[i] : v)));
  return [det3(replace(0)) / D, det3(replace(1)) / D, det3(replace(2)) / D];
}
